import { jsPDF } from 'jspdf';
import { I18N } from './i18n';
import { APP_INFO, getLogo } from './license';
import { clean, loadImg, download, projectStem } from './exporters';
import { getCompany, companyLine } from './company';
import { planBounds } from '../components/FloorPlans';
import { allStats, moUnit, wallGroups } from './moist';
import { moistBlock, moistTrendBlock, moistPins } from './moistPdf';
import { mo2 } from './i18nMoist2';

const nf = (v, d = 0) => (+v).toFixed(d).replace('.', ',');
const imgOf = (doc, src) => { try { return doc.getImageProperties(src); } catch { return null; } };

// one-click damage report for insurers: claim data, cause, measures, leaks with area + photos, plans, moisture maps, signature
export async function damagePdf(project, lang) {
  const t = I18N[lang] || I18N.de, dm = project.damage || {}, sig = project.signature || {}, co = getCompany();
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, H = 297, M = 15, CW = W - 2 * M, BOT = H - 18;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(t.locale) : '-');
  const logo = getLogo(), li = logo ? await loadImg(logo, false) : null;
  let y = 0;
  const head = () => {
    doc.setFillColor(14, 30, 48); doc.rect(0, 0, W, 24, 'F');
    if (li) { const r = li.width / li.height, w = Math.min(36, r * 14); doc.addImage(logo, 'PNG', W - M - w, 5, w, w / r); }
    doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.text(clean(t.dmTitle), M, 11);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.text(clean(co.name ? companyLine(co) : APP_INFO.publisher).slice(0, 110), M, 18);
    doc.setTextColor(20, 20, 20); y = 32;
  };
  const page = () => { doc.addPage(); head(); };
  const ensure = (h) => { if (y + h > BOT) page(); };
  const section = (s) => { ensure(12); doc.setFillColor(241, 245, 249); doc.rect(M, y - 4.5, CW, 7, 'F'); doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.text(clean(s), M + 2, y); y += 7; };
  const para = (s, size = 9) => { doc.setFont('helvetica', 'normal'); doc.setFontSize(size); doc.splitTextToSize(clean(s || '-'), CW).forEach((l) => { ensure(5); doc.text(l, M, y); y += size * 0.45; }); y += 2; };
  const kv = (k, v) => { doc.setFontSize(9); const ls = doc.splitTextToSize(clean(String(v || '-')), CW - 52); ensure(ls.length * 4.4 + 2); doc.setFont('helvetica', 'bold'); doc.text(clean(k), M, y); doc.setFont('helvetica', 'normal'); doc.text(ls, M + 52, y); y += Math.max(5.6, ls.length * 4.4 + 1.2); };
  const pic = (src, maxW, maxH, x = M) => { const p = imgOf(doc, src); if (!p) return 0; const s = Math.min(maxW / p.width, maxH / p.height), w = p.width * s, h = p.height * s; try { doc.addImage(src, 'JPEG', x, y, w, h); } catch { return 0; } return h; };

  head();
  const leaks = (project.fittings || []).filter((f) => f.type === 'leak');
  const total = leaks.reduce((s, f) => s + (+f.leakCalc?.photoArea || 0), 0);
  section(t.dmData);
  kv(t.dmInsurer, dm.insurer); kv(t.dmClaim, dm.claim); kv(t.dmPolicy, dm.policy); kv(t.dmHolder, dm.holder);
  kv(t.dmPlace, dm.place || project.address || project.reports?.[0]?.address); kv(t.dmDate, fmt(dm.date)); kv(t.dmVisit, fmt(dm.visit));
  kv(t.dmExpert, [sig.name, sig.role].filter(Boolean).join(', ') || co.name);
  y += 2; section(t.dmSummary);
  const moist = project.moist || [], ms = moist.map(allStats).filter(Boolean), wetMax = ms.length ? Math.max(...ms.map((s) => s.max)) : null;
  para(`${t.dmLeaks}: ${leaks.length}${total ? ` · ${t.dmTotalArea}: ${nf(total)} mm²` : ''} · ${t.dmMoist}: ${moist.length}${wetMax != null ? ` · ${t.dmWetMax}: ${nf(wetMax, 1)} ${moUnit(moist[0])}` : ''} · ${t.dmPlans}: ${(project.plans || []).length}`);
  section(t.dmCause); para(dm.cause);
  section(t.dmMeasures); para(dm.measures);

  section(t.dmLeaks);
  if (!leaks.length) para(t.dmNoLeaks);
  leaks.forEach((f, i) => {
    ensure(16);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.text(clean(`${i + 1}. ${f.name || t.dmLeaks}`), M, y); y += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    doc.text(clean(`${t.dmSev}: ${t['sev_' + (f.severity || 'medium')] || '-'} · ${t.dmArea}: ${f.leakCalc?.photoArea ? `${nf(f.leakCalc.photoArea)} mm²` : '-'}${f.lm?.length ? ` · ${t.lmTitle}: ${f.lm.map((k) => t['lm_' + k]).join(', ')}` : ''} · ${(+f.lat).toFixed(6)}, ${(+f.lon).toFixed(6)}`), M, y); y += 4.6;
    if (f.note) para(f.note, 8.5);
    const ph = [...(f.media || []).filter((m) => m.kind === 'photo' && m.data).map((m) => m.data), ...(f.photos || []).map((p) => p.src || p.data)].filter(Boolean).slice(0, 4);
    if (ph.length) { ensure(52); let mh = 0; ph.forEach((s, k) => { mh = Math.max(mh, pic(s, (CW - 9) / 4, 48, M + k * ((CW - 9) / 4 + 3))); }); y += mh + 4; }
  });

  for (const pl of project.plans || []) {
    page(); section(`${t.dmPlans}: ${pl.name}`);
    let w = CW, h = (CW * pl.h) / pl.w;
    if (h > 205) { h = 205; w = (205 * pl.w) / pl.h; }
    const x0 = M + (CW - w) / 2, y0 = y, [[s, wv], [n, e]] = planBounds(pl);
    const P = (la, lo) => [x0 + ((lo - wv) / (e - wv)) * w, y0 + ((n - la) / (n - s)) * h];
    try { doc.addImage(pl.src, 'JPEG', x0, y0, w, h); } catch { /* unreadable plan */ }
    doc.setDrawColor(148, 163, 184); doc.setLineWidth(0.2); doc.rect(x0, y0, w, h);
    leaks.forEach((f, i) => { if (f.floor && f.floor !== pl.id) return; const [x, yy] = P(f.lat, f.lon); if (x < x0 || x > x0 + w || yy < y0 || yy > y0 + h) return; doc.setFillColor(220, 38, 38); doc.circle(x, yy, 2.2, 'F'); doc.setTextColor(255); doc.setFontSize(7); doc.text(String(i + 1), x, yy + 0.9, { align: 'center' }); });
    const mpins = moistPins(doc, wallGroups(moist), pl.id, P, [x0, y0, x0 + w, y0 + h]);
    doc.setTextColor(20, 20, 20); y = y0 + h + 6;
    if (mpins.length) { doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.text(clean(mo2().pinT), M, y); y += 4.5; mpins.forEach((s) => para(s, 8)); }
  }

  for (const m of moist) {
    page(); section(`${t.dmMoist}: ${m.name}`);
    await moistBlock(doc, m, { M, CW, t, getY: () => y, setY: (v) => { y = v; }, ensure, maxH: 150 });
  }
  for (const s of wallGroups(moist).filter((g) => g.length > 1)) {
    page(); section(`${mo2().trend}: ${s[0].name}`);
    await moistTrendBlock(doc, s, { M, CW, t, getY: () => y, setY: (v) => { y = v; }, ensure, maxH: 105 });
  }

  ensure(42); y = Math.max(y + 6, Math.min(BOT - 36, y + 6));
  section(t.dmSign);
  if (sig.data) { const h = pic(sig.data, 70, 24); y += h + 2; } else { doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.text(clean(t.dmNoSig), M, y + 10); y += 18; }
  doc.setDrawColor(120); doc.setLineWidth(0.3); doc.line(M, y, M + 80, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(90);
  doc.text(clean(`${[sig.name, sig.role].filter(Boolean).join(', ')}${sig.at ? ` · ${new Date(sig.at).toLocaleString(t.locale)}` : ''}`), M, y + 4.5);
  doc.text(clean(t.dmPlaceDate), M, y + 9);

  const N = doc.getNumberOfPages();
  for (let i = 1; i <= N; i++) { doc.setPage(i); doc.setFontSize(7.5); doc.setTextColor(110); doc.text(clean(`${companyLine(co) || APP_INFO.name}`).slice(0, 120), M, H - 8); doc.text(`${clean(t.dmPage)} ${i}/${N}`, W - M, H - 8, { align: 'right' }); }
  const name = `${projectStem(project)}_${t.dmFile}.pdf`;
  download(doc.output('blob'), name);
  return name;
}
